'use client';

import { c as _c } from "react/compiler-runtime";
import { isServer, matchQuery, MutationCache, QueryClient, QueryClientProvider, type QueryKey } from '@tanstack/react-query';
import React from 'react';
import { TanstackDevTools } from '../devtools';
declare module '@tanstack/query-core' {
  interface Register {
    mutationMeta: {
      invalidates?: Array<QueryKey>;
    };
  }
}
export function makeQueryClient() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        // Returning to a page renders from cache: fresh data is not refetched on
        // remount, and unused data outlives a normal trip to another page
        gcTime: 30 * 60 * 1000,
        staleTime: 30 * 1000
      }
    },
    mutationCache: new MutationCache({
      onSuccess: (_data, _variables, _context, mutation) => {
        queryClient.invalidateQueries({
          predicate: query =>
          // invalidate all matching tags at once; a mutation without tags owns its
          // own cache updates, so it invalidates nothing here
          mutation.meta?.invalidates?.some(queryKey => matchQuery({
            queryKey
          }, query)) ?? false
        });
      }
    })
  });
  return queryClient;
}
let browserQueryClient: QueryClient | undefined = undefined;
function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}
export const TanstackProvider = t0 => {
  const $ = _c(4);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getQueryClient();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const queryClient = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <TanstackDevTools data-sentry-element="TanstackDevTools" data-sentry-source-file="tanstack-provider.tsx" />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children) {
    t3 = <QueryClientProvider client={queryClient} data-sentry-element="QueryClientProvider" data-sentry-component="TanstackProvider" data-sentry-source-file="tanstack-provider.tsx">{children}{t2}</QueryClientProvider>;
    $[2] = children;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};